import { Card, List, Tag, Tooltip, Typography } from "antd"
import React from "react"
import {
  InboxOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons"
import { useRegister } from "../../../contexts/RegisterContext"

const { Paragraph, Title, Text } = Typography

function CardListView(props: any) {
  const { register } = useRegister()

  const getCategoryName = (category: string) => {
    const moduleOption = register.categoryOptions.find(
      (option) => option.type === "CATEGORY_OPTION" && option.key === category
    )
    return moduleOption?.string_value || category || ""
  }

  const renderStockBadge = (item: any) => {
    if (item.register_type === "SERVICE") {
      return (
        <Tag color="cyan" style={{ margin: 0, borderRadius: 4 }}>
          Servicio
        </Tag>
      )
    }

    const total =
      !item.inventory || item.inventory.length === 0
        ? 0
        : item.inventory.reduce((sum: number, entry: any) => sum + (entry.quantity || 0), 0)

    const minQuantity = item.min_quantity ?? 0
    const unitLabel = item.display || item.measure_unit || "UND"

    if (total < 0) {
      return (
        <Tag color="error" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
          <CloseCircleOutlined /> {total} {unitLabel}
        </Tag>
      )
    }
    if (total === 0) {
      return (
        <Tag color="warning" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
          <ExclamationCircleOutlined /> Agotado
        </Tag>
      )
    }
    if (minQuantity > 0 && total <= minQuantity) {
      return (
        <Tag color="gold" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
          <WarningOutlined /> {total} {unitLabel} (Bajo)
        </Tag>
      )
    }
    return (
      <Tag color="success" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
        <CheckCircleOutlined /> {total} {unitLabel}
      </Tag>
    )
  }

  return (
    <List
      grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 6 }}
      dataSource={props.dataTable}
      pagination={props.pagination}
      renderItem={(item: any, index: number) => {
        const primaryImg = item.images?.find((img: any) => img.is_primary) || item.images?.[0]
        const imageUrl = item.image || item.image_mini || primaryImg?.file_url
        const categoryName = getCategoryName(item.category)
        const displayUnit = item.display || item.measure_unit

        return (
          <List.Item style={{ marginBottom: 16 }}>
            <Card
              hoverable
              onClick={() => {
                if (props.onClick) {
                  const rowHandler = props.onClick(item, index)
                  if (typeof rowHandler?.onClick === "function") {
                    rowHandler.onClick()
                  }
                }
              }}
              style={{
                height: 360,
                display: "flex",
                flexDirection: "column",
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid #f0f0f0",
              }}
              styles={{
                body: {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: "12px 14px",
                  justifyContent: "space-between",
                },
              }}
              cover={
                <div
                  style={{
                    height: 160,
                    position: "relative",
                    background: "#fbfbfb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  {imageUrl ? (
                    <img
                      alt={item.name}
                      src={imageUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        padding: 8,
                      }}
                      onError={(e) => {
                        ;(e.target as HTMLElement).style.display = "none"
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#bfbfbf" }}>
                      <InboxOutlined style={{ fontSize: 36 }} />
                      <div style={{ fontSize: 11, marginTop: 4 }}>Sin imagen</div>
                    </div>
                  )}

                  <div style={{ position: "absolute", top: 8, right: 8 }}>
                    {renderStockBadge(item)}
                  </div>
                </div>
              }
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#8c8c8c",
                    marginBottom: 2,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  ID: {item.code || item.custom_id || "—"}
                </div>
                <Tooltip title={item.name}>
                  <Title
                    level={5}
                    ellipsis={{ rows: 2 }}
                    style={{ marginBottom: 4, lineHeight: "1.3em", fontSize: 14 }}
                  >
                    {item.name}
                  </Title>
                </Tooltip>
                {item.description && (
                  <Tooltip title={item.description}>
                    <Paragraph
                      ellipsis={{ rows: 1 }}
                      style={{ marginBottom: 6, color: "rgba(0,0,0,0.5)", fontSize: 12 }}
                    >
                      {item.description}
                    </Paragraph>
                  </Tooltip>
                )}
              </div>

              <div style={{ marginTop: "auto", paddingTop: 6 }}>
                {(() => {
                  const sellPrice = item.prices?.find((p: any) => p.type === "SELL")?.value ?? item.sell_price
                  const purchasePrice = item.prices?.find((p: any) => p.type === "PURCHASE")?.value ?? item.purchase_price
                  if (sellPrice !== undefined && sellPrice !== null && Number(sellPrice) > 0) {
                    return (
                      <Text strong style={{ fontSize: 15, color: "#1890ff", display: "block", marginBottom: 6 }}>
                        ${Number(sellPrice).toLocaleString("es-CO")} COP
                      </Text>
                    )
                  }
                  if (purchasePrice !== undefined && purchasePrice !== null && Number(purchasePrice) > 0) {
                    return (
                      <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
                        ${Number(purchasePrice).toLocaleString("es-CO")} COP (compra)
                      </Text>
                    )
                  }
                  return (
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                      Precio no asignado
                    </Text>
                  )
                })()}

                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6, alignItems: "center" }}>
                  {categoryName && (
                    <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
                      {categoryName}
                    </Tag>
                  )}
                  {(() => {
                    const isService = item.register_type === "SERVICE"
                    const measureUnit = item.measure_unit || "UND"
                    const display = item.display

                    if (isService) {
                      return (
                        <Tag color="default" style={{ borderRadius: 4, margin: 0, fontSize: 11, fontWeight: 500 }}>
                          {measureUnit}
                        </Tag>
                      )
                    }

                    if (!display || display.toUpperCase() === measureUnit.toUpperCase()) {
                      return (
                        <Tag color="default" style={{ borderRadius: 4, margin: 0, fontSize: 11, fontWeight: 500 }}>
                          {display || measureUnit}
                        </Tag>
                      )
                    }

                    const factor = item.conversion_factor && Number(item.conversion_factor) > 1
                      ? `${item.conversion_factor} `
                      : ""

                    return (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontSize: 11, fontWeight: 500 }}>
                          {display}
                        </Tag>
                        <span style={{ fontSize: 10.5, color: "#8c8c8c" }}>
                          ({factor}{measureUnit})
                        </span>
                      </span>
                    )
                  })()}
                </div>

                {item.location && (
                  <Text type="secondary" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}>
                    <EnvironmentOutlined style={{ fontSize: 11 }} /> {item.location}
                  </Text>
                )}
              </div>
            </Card>
          </List.Item>
        )
      }}
    />
  )
}

export default CardListView